import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useSpring, useTransform } from 'framer-motion';
import { Github, Linkedin, Code, TestTube, Cpu, Globe, ChevronRight, ExternalLink, Menu, X, Brain, Briefcase, MapPin, ArrowUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Scene3D from '../components/Scene3D';
import TiltCard from '../components/TiltCard';
import Magnetic from '../components/Magnetic';

import microsoftLogo from '../assets/certs/microsoft.jpg';
import istqbLogo from '../assets/certs/istqb.jpg';
import udacityLogo from '../assets/certs/udacity.jpg';
import accentureLogo from '../assets/certs/accenture.jpg';
import tauLogo from '../assets/certs/testautomationu.jpg';

const certificates = [
  {
    title: 'Azure Fundamentals (AZ-900)',
    issuer: 'Microsoft',
    date: 'Jan 2023',
    logo: microsoftLogo,
    accent: '#0ea5e9',
    url: 'https://learn.microsoft.com/tr-tr/users/emredaver-1819/credentials/5c0656292c99c21a?ref=https%3A%2F%2Fwww.linkedin.com%2F',
  },
  {
    title: 'ISTQB® Certified Tester — Foundation Level',
    issuer: 'ISTQB®',
    date: '',
    logo: istqbLogo,
    accent: '#1d4ed8',
    url: 'https://app.diplomasafe.com/en-US/certificates/dd2966608283321653efce22e836c213e34681b74',
  },
  {
    title: 'Building Agents',
    issuer: 'Udacity',
    date: '',
    logo: udacityLogo,
    accent: '#4338ca',
    url: 'https://www.udacity.com/certificate/e/7398be1c-e0c8-11f0-ad90-df809efed4b2',
  },
  {
    title: 'Reinvention with Agentic AI',
    issuer: 'Accenture',
    date: '',
    logo: accentureLogo,
    accent: '#a020f0',
    url: 'https://www.credly.com/badges/ec0c2691-2a55-4ab3-aa8c-f948096fa8a7/linked_in_profile',
  },
  {
    title: 'Selenium WebDriver with Java',
    issuer: 'Test Automation University',
    date: '2022',
    logo: tauLogo,
    accent: '#f97316',
    url: 'https://testautomationu.applitools.com/certificate/?id=659db01f',
  },
];

// Horizontally draggable certificates carousel
const CertCarousel = () => {
  const trackRef = useRef(null);
  const didDragRef = useRef(false);
  const [constraint, setConstraint] = useState(0);

  useEffect(() => {
    const measure = () => {
      const el = trackRef.current;
      if (!el) return;
      setConstraint(Math.max(0, el.scrollWidth - el.offsetWidth));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const openCredential = (url) => (e) => {
    // Ignore the click that fires right after a drag gesture
    if (didDragRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="relative">
      <motion.div ref={trackRef} className="overflow-hidden cursor-grab active:cursor-grabbing py-2">
        <motion.div
          className="flex gap-5 sm:gap-6 w-max"
          drag="x"
          dragConstraints={{ left: -constraint, right: 0 }}
          dragElastic={0.08}
          onDragStart={() => { didDragRef.current = true; }}
          onDragEnd={() => { setTimeout(() => { didDragRef.current = false; }, 60); }}
          whileTap={{ cursor: 'grabbing' }}
        >
          {certificates.map((c, i) => (
            <motion.div
              key={i}
              data-testid={`cert-card-${i}`}
              className="group relative shrink-0 w-[280px] sm:w-[320px] rounded-2xl p-6 bg-white/70 backdrop-blur-xl border border-slate-200/80 shadow-[0_8px_30px_rgba(2,6,23,0.06)] hover:shadow-[0_20px_50px_rgba(2,6,23,0.14)] transition-shadow duration-300 select-none"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            >
              <span
                className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
                style={{ background: `linear-gradient(90deg, ${c.accent}, transparent)` }}
              />
              <div className="flex items-center justify-between mb-5">
                <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center overflow-hidden ring-1 ring-slate-200 shadow-sm">
                  <img src={c.logo} alt={`${c.issuer} logo`} className="w-10 h-10 object-contain pointer-events-none" draggable={false} />
                </div>
                {c.date && (
                  <span
                    className="text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={{ color: c.accent, backgroundColor: `${c.accent}14` }}
                  >
                    {c.date}
                  </span>
                )}
              </div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug min-h-[3.25rem]">{c.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{c.issuer}</p>
              <button
                type="button"
                onClick={openCredential(c.url)}
                data-testid={`cert-view-${i}`}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-white px-3 py-1.5 rounded-lg border border-slate-200 hover:border-transparent transition-colors"
                style={{ '--tw-hover-bg': c.accent }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.accent; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                View credential
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white/80 to-transparent hidden sm:block" />
    </div>
  );
};


// LED dot-matrix scrolling ticker
const LedTicker = () => {
  const phrases = [
    { text: 'Quality is built in, not inspected in.', cls: 'led-cyan' },
    { text: 'Catching bugs is good; preventing them is better.', cls: 'led-amber' },
  ];
  const sequence = [...phrases, ...phrases]; // duplicate for seamless loop
  return (
    <div className="led-board" data-testid="led-ticker">
      <div className="led-track">
        {sequence.map((p, i) => (
          <span key={i} className="led-phrase">
            <span className={p.cls}>{p.text}</span>
            <span className="led-dot">{'  \u2022  '}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// Scroll reveal wrapper
const Reveal = ({ children, delay = 0, y = 40, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [showTop, setShowTop] = useState(false);
  const scrollRef = useRef(0);

  const sections = [
    { id: 'hero', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'experience', label: 'Experience' },
    { id: 'expertise', label: 'Expertise' },
    { id: 'projects', label: 'Projects' },
    { id: 'skills', label: 'Skills' },
  ];

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const { scrollYProgress } = useScroll();
  const progressScale = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });

  // Scroll-driven timeline fill for the Experience section
  const timelineRef = useRef(null);
  const { scrollYProgress: timelineProgress } = useScroll({
    target: timelineRef,
    offset: ['start 75%', 'end 55%'],
  });
  const fillHeight = useTransform(timelineProgress, [0, 1], ['0%', '100%']);
  const fillHeightSpring = useSpring(fillHeight, { stiffness: 90, damping: 25, restDelta: 0.001 });

  useEffect(() => {
    const onScroll = () => {
      scrollRef.current = window.scrollY;
      setShowTop(window.scrollY > 500);
      // Scroll spy: the active section is the last one whose top has passed
      // just below the fixed navbar (probe line).
      const probe = 140;
      let current = sections[0].id;
      for (const { id } of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= probe) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skills = [
    { category: 'Test Automation', items: ['Selenium', 'Playwright', 'Rest Assured', 'Locust', 'JMeter'] },
    { category: 'Programming', items: ['Python', 'Java', 'JavaScript'] },
    { category: 'Testing Types', items: ['API Testing', 'Web App Testing', 'Mobile Testing', 'Desktop App Testing', 'AI Application Testing', 'E2E Testing', 'Performance Testing', 'Firmware Testing', 'IoT Testing', 'Regression Testing', 'Exploratory Testing', 'Risk-Based Testing'] },
    { category: 'Tools & Frameworks', items: ['TestNG', 'JUnit', 'Pytest', 'Behave', 'Allure', 'Jenkins', 'GitLab', 'Git', 'Postman', 'Sphinx', 'Azure DevOps', 'Azure'] },
    { category: 'Methodologies & Practices', items: ['Agile', 'Scrum', 'Kanban', 'CI/CD', 'BDD', 'Shift-Left Testing', 'Defect Life Cycle Management', 'Test Data Management'] },
  ];

  const projects = [
    {
      id: 1,
      title: 'Resource Checker',
      description: 'System monitoring tool that tracks CPU, RAM, and network usage in real-time. Includes logging and resource consumption analytics.',
      image: 'https://customer-assets.emergentagent.com/job_fe4d3b4d-e780-4b84-9fbe-7f3a5a4460e9/artifacts/gf6dr2t1_523491733-00e026d7-54aa-4de8-ba9d-96e0f1c0602b.png',
      github: 'https://github.com/emredav/resource-checker',
      tags: ['Python', 'System Monitoring', 'Performance'],
    },
    {
      id: 2,
      title: 'JSON Editor',
      description: 'JSON manipulation tool with schema validation, path analyzer, and tree visualization. Helps with JSON data handling for testing and development.',
      image: 'https://customer-assets.emergentagent.com/job_fe4d3b4d-e780-4b84-9fbe-7f3a5a4460e9/artifacts/nohpdu8d_Screenshot_20260222_204157_Chrome.jpg',
      github: 'https://github.com/emredav/json-editor',
      tags: ['JavaScript', 'Web Tool', 'Testing Utility'],
    },
    {
      id: 3,
      title: 'Daily Wheels',
      description: 'Daily standup randomizer application. Adds a spin wheel interface for selecting speakers in team meetings.',
      image: 'https://customer-assets.emergentagent.com/job_fe4d3b4d-e780-4b84-9fbe-7f3a5a4460e9/artifacts/m1pzhjal_Screenshot_20260222_204400_Chrome.jpg',
      github: 'https://github.com/emredav/daily-wheels',
      tags: ['JavaScript', 'Team Tool', 'Interactive'],
    },
    {
      id: 4,
      title: 'Locust Performance Testing Framework',
      description: 'Python-based performance testing framework using Locust for load testing frequently accessed screens and user workflows.',
      image: 'https://customer-assets.emergentagent.com/job_test-automation-hub-13/artifacts/yhu30r3a_Screenshot_20260222_211543_Chrome.jpg',
      github: null,
      tags: ['Python', 'Locust', 'Performance Testing', 'Load Testing'],
    },
  ];

  const expertise = [
    {
      icon: <TestTube className="w-8 h-8" />,
      title: 'Test Automation',
      description: 'Designing and implementing test automation frameworks for web, mobile, and API testing.',
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: 'Manual Testing',
      description: 'Functional and usability testing for desktop, web, and mobile applications.',
    },
    {
      icon: <Cpu className="w-8 h-8" />,
      title: 'IoT & Performance Testing',
      description: 'Firmware testing for IoT devices and performance testing for web applications and embedded systems.',
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: 'API Testing',
      description: 'Manual and automated API testing for functionality and performance.',
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: 'AI Application Testing',
      description: 'Testing of AI-powered applications including anomaly detection, predictive maintenance, and chatbot systems.',
    },
  ];

  const experience = [
    {
      company: 'Accenture',
      duration: 'Sep 2025 - Present · 1 yr',
      roles: [
        {
          title: 'QA Engineer',
          type: 'Full-time',
          period: 'Sep 2025 - Present · 1 yr',
          location: null,
          points: [
            'BDD & Test Execution: Writing, updating, and executing BDD-based test cases in Gherkin, improving requirement clarity and collaboration between QA, developers, and stakeholders.',
            'Defect Management: Reporting identified defects and re-testing fixed defects.',
            'Framework Maintenance & Unit Testing: Maintaining the Test Automation Project and writing unit tests using Pytest to ensure code reliability and test framework stability.',
            'E2E Automation: Contributing to an end-to-end automated testing framework built with Python, Playwright, Behave (BDD), Allure, AWS, GitLab, Sphinx, and Pytest.',
          ],
          skills: ['QA', 'Software Testing', '+14 skills'],
        },
      ],
    },
    {
      company: 'Doruk',
      duration: '3 yrs 1 mo',
      roles: [
        {
          title: 'Software Test Engineer',
          type: 'Full-time',
          period: 'Aug 2023 - Sep 2025 · 2 yrs 2 mos',
          location: null,
          intro: 'Working alongside cross-functional teams, I contributed to end-to-end testing efforts across web, mobile, and IoT environments for MES/MOM software. We built automated testing infrastructures to support the software development life cycle.',
          points: [
            'Conducted functional and firmware tests for IoT devices (Smart Counter) designed for data collection.',
            'Performed functional tests on the KeyPDT used for collecting production data and serving as an operator interface.',
            'Created and tracked bug reports in Azure DevOps for pre-release testing and production issue triage.',
            'Developed UI test automation for web applications using Java, Selenium, TestNG, and Spring Boot.',
            'Developed an API automation project using Java and RestAssured.',
            'Reviewed automation test results (Allure Reports etc.) and took necessary actions.',
            'Analyzed project requirements and identified areas for improvement.',
            'Conducted usability testing to enhance user experience (UX).',
            'Supported testing efforts for various AI applications.',
            'Performed functional and usability testing for mobile, desktop and cloud applications.',
            'Executed regression tests prior to deployments.',
            'Developed automated performance testing in Python with Locust, integrated with the Jenkins pipeline.',
            'Developed a PowerShell CLI tool to streamline manual testing processes.',
            'Tested AI applications including Anomaly Detection, Predictive Maintenance and Chatbot systems.',
          ],
          skills: ['QA', 'Web UI Testing', '+26 skills'],
        },
        {
          title: 'Software Test Engineer',
          type: 'Part-time',
          period: 'Nov 2022 - Aug 2023 · 10 mos',
          location: 'İstanbul, Türkiye',
          points: [],
          skills: ['Computer Science', 'SDLC', '+3 skills'],
        },
        {
          title: 'Software Testing Intern',
          type: 'Internship',
          period: 'Sep 2022 - Oct 2022 · 2 mos',
          location: 'İstanbul, Türkiye',
          points: [],
          skills: [],
        },
      ],
    },
  ];

  const navLinks = [
    { href: '#about', label: 'About' },
    { href: '#experience', label: 'Experience' },
    { href: '#expertise', label: 'Expertise' },
    { href: '#projects', label: 'Projects' },
    { href: '#skills', label: 'Skills' },
  ];

  return (
    <div className="relative min-h-screen text-slate-900" data-testid="home-page">
      {/* Immersive 3D background */}
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50" data-testid="scene-3d-background">
        <Scene3D scrollRef={scrollRef} />
      </div>

      {/* Scroll progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 origin-left z-[60] bg-gradient-to-r from-blue-600 to-cyan-500"
        style={{ scaleX: progressScale }}
        data-testid="scroll-progress-bar"
      />

      {/* Section dot navigation (scroll-spy + quick jump) */}
      <div className="fixed right-4 xl:right-6 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col gap-4" data-testid="section-dots">
        {sections.map((s) => {
          const isActive = activeSection === s.id;
          return (
            <button
              key={s.id}
              onClick={() => scrollToSection(s.id)}
              className="group relative flex items-center justify-end"
              aria-label={`Go to ${s.label}`}
              data-testid={`section-dot-${s.id}`}
            >
              <span className="absolute right-6 whitespace-nowrap text-xs font-medium text-slate-700 bg-white/80 backdrop-blur px-2 py-1 rounded-md shadow-sm opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all pointer-events-none">
                {s.label}
              </span>
              <span
                className={`block rounded-full transition-all duration-300 ${
                  isActive ? 'w-3.5 h-3.5 bg-blue-600 shadow-md shadow-blue-500/50' : 'w-2.5 h-2.5 bg-slate-300 group-hover:bg-blue-400'
                }`}
              />
            </button>
          );
        })}
      </div>

      {/* Back to top */}
      <AnimatePresence>
        {showTop && (
          <motion.div
            initial={{ opacity: 0, scale: 0.6, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 20 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-6 right-6 z-50"
          >
            <Magnetic strength={0.5}>
              <button
                onClick={() => scrollToSection('hero')}
                className="w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/40 flex items-center justify-center transition-colors"
                aria-label="Back to top"
                data-testid="back-to-top-button"
              >
                <ArrowUp className="w-5 h-5" />
              </button>
            </Magnetic>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40 shadow-sm" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent" data-testid="nav-logo">
              Emre Daver
            </div>

            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => {
                const id = link.href.replace('#', '');
                const isActive = activeSection === id;
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    className={`transition-colors font-medium relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:bg-blue-600 after:transition-all ${
                      isActive ? 'text-blue-600 after:w-full' : 'text-slate-700 hover:text-blue-600 after:w-0 hover:after:w-full'
                    }`}
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    {link.label}
                  </a>
                );
              })}
              <a
                href="https://github.com/emredav"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-700 hover:text-slate-900 transition-colors"
                aria-label="GitHub"
                data-testid="nav-github-link"
              >
                <Github className="w-5 h-5" />
              </a>
              <Magnetic strength={0.4}>
                <Button
                  onClick={() => window.open('https://www.linkedin.com/in/emre-daver', '_blank')}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="nav-connect-button"
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  Connect
                </Button>
              </Magnetic>
            </div>

            <button
              className="md:hidden p-2 text-slate-700 hover:text-blue-600 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3" data-testid="mobile-menu">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block text-slate-700 hover:text-blue-600 transition-colors font-medium py-2"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ))}
              <Button
                variant="outline"
                onClick={() => {
                  window.open('https://github.com/emredav', '_blank');
                  setMobileMenuOpen(false);
                }}
                className="w-full border-slate-300"
                data-testid="mobile-github-button"
              >
                <Github className="w-4 h-4 mr-2" />
                GitHub
              </Button>
              <Button
                onClick={() => {
                  window.open('https://www.linkedin.com/in/emre-daver', '_blank');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="mobile-connect-button"
              >
                <Linkedin className="w-4 h-4 mr-2" />
                Connect
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6 relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              className="space-y-6 sm:space-y-8"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="inline-block">
                <Badge className="bg-blue-100/80 backdrop-blur text-blue-700 hover:bg-blue-200 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm" data-testid="hero-badge">
                  Software Test Engineer & QA Professional
                </Badge>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-900 leading-tight">
                Software Testing &
                <span className="block sm:inline bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent"> Test Automation</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-slate-600 leading-relaxed">
                Computer Engineer working in Software Testing & Test Automation. My expertise spans across testing web, desktop, mobile, and AI applications. Committed to continuous learning, I actively leverage modern AI tools to streamline and optimize QA processes. I enjoy working closely with stakeholders to build reliable software and deliver a great user experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Magnetic strength={0.35} className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    onClick={() => window.open('https://www.linkedin.com/in/emre-daver', '_blank')}
                    className="bg-blue-600 hover:bg-blue-700 group w-full sm:w-auto"
                    data-testid="hero-linkedin-button"
                  >
                    <Linkedin className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                    LinkedIn Profile
                    <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Magnetic>
                <Magnetic strength={0.35} className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => window.open('https://github.com/emredav', '_blank')}
                    className="border-slate-300 bg-white/60 backdrop-blur hover:border-slate-900 hover:text-slate-900 w-full sm:w-auto"
                    data-testid="hero-github-button"
                  >
                    <Github className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                    GitHub
                  </Button>
                </Magnetic>
                <Magnetic strength={0.35} className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => scrollToSection('projects')}
                    className="border-slate-300 bg-white/60 backdrop-blur hover:border-blue-600 hover:text-blue-600 w-full sm:w-auto"
                    data-testid="hero-projects-button"
                  >
                    View Projects
                  </Button>
                </Magnetic>
              </div>
            </motion.div>
            <motion.div
              className="relative mt-8 lg:mt-0"
              initial={{ opacity: 0, scale: 0.9, rotateY: 20 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              style={{ perspective: 1000 }}
            >
              <TiltCard max={10}>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-2xl sm:rounded-3xl blur-3xl"></div>
                <img
                  src="https://images.unsplash.com/photo-1763568258598-3ab9cf2516d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxzb2Z0d2FyZSUyMHRlc3RpbmclMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3MTc4MjQ4NHww&ixlib=rb-4.1.0&q=85"
                  alt="Software Testing"
                  className="relative rounded-xl sm:rounded-2xl shadow-2xl w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
                  data-testid="hero-image"
                />
              </TiltCard>
            </motion.div>
          </div>

          {/* Featured Certificates — draggable, within Home */}
          <motion.div
            className="mt-16 sm:mt-24"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="certificates-block"
          >
            <div className="flex items-center justify-between mb-5 sm:mb-6 gap-4">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Featured Certificates</h2>
              <p className="text-xs sm:text-sm text-slate-500 flex items-center gap-1.5 shrink-0">
                <span>Drag to explore</span>
                <span className="inline-flex items-center text-slate-400">
                  <ChevronRight className="w-4 h-4 -mr-2.5 animate-pulse" />
                  <ChevronRight className="w-4 h-4 animate-pulse" />
                </span>
              </p>
            </div>
            <CertCarousel />
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-20 px-4 sm:px-6 bg-white relative z-10" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6">
            <Reveal>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">About Me</h2>
              <div className="h-1 w-20 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full mt-4"></div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-sm sm:text-base lg:text-lg text-slate-600 leading-relaxed text-left mt-6 sm:mt-8">
                Computer Engineer who started working in software testing during senior year at Bahçeşehir University.
                Over the years, I've expanded my skills across numerous projects, covering end-to-end quality assurance
                for web, desktop, and mobile platforms. My work blends manual usability assessments, API and E2E automation,
                and IoT firmware testing.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-sm sm:text-base lg:text-lg text-slate-600 leading-relaxed text-left">
                I handle performance testing as a separate key discipline, evaluating speed, responsiveness, and system
                stability under load for web and IoT environments. Always eager to follow new tech trends and collaborate
                directly with dev teams to ship reliable products.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25} className="max-w-4xl mx-auto mt-10 sm:mt-12">
            <LedTicker />
          </Reveal>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-16 sm:py-20 px-4 sm:px-6 bg-transparent relative z-0" data-testid="experience-section">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center space-y-3 sm:space-y-4 mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Experience</h2>
            <div className="h-1 w-20 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full"></div>
            <p className="text-sm sm:text-base lg:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              My professional journey in software testing and quality assurance
            </p>
          </Reveal>

          <div ref={timelineRef} className="relative ml-3 sm:ml-6 space-y-10">
            {/* Timeline track + scroll-driven animated fill */}
            <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-blue-100" />
            <motion.div
              className="absolute left-0 top-0 w-[2px] bg-gradient-to-b from-blue-600 to-cyan-500 origin-top rounded-full"
              style={{ height: fillHeightSpring }}
              data-testid="experience-timeline-fill"
            />
            {experience.map((company, ci) => (
              <div key={ci} className="relative" data-testid={`experience-company-${ci}`}>
                <div className="pl-6 sm:pl-10">
                  <motion.div
                    className="absolute -left-[11px] top-1 w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 ring-4 ring-white shadow-lg shadow-blue-500/40"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  />
                  <motion.div
                    className="flex items-center gap-2 mb-6"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Briefcase className="w-5 h-5 text-blue-600" />
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900">{company.company}</h3>
                    <span className="text-sm text-slate-500">· {company.duration}</span>
                  </motion.div>

                  <div className="space-y-5" style={{ perspective: 1200 }}>
                    {company.roles.map((role, ri) => (
                      <motion.div
                        key={ri}
                        initial={{ opacity: 0, x: -70, rotateY: -14, filter: 'blur(6px)' }}
                        whileInView={{ opacity: 1, x: 0, rotateY: 0, filter: 'blur(0px)' }}
                        viewport={{ once: true, margin: '-90px' }}
                        transition={{ duration: 0.7, delay: ri * 0.08, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <TiltCard max={6}>
                          <Card className="border-slate-200 bg-white/90 backdrop-blur hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300" data-testid={`experience-role-${ci}-${ri}`}>
                            <CardHeader className="pb-3">
                              <CardTitle className="text-lg sm:text-xl text-slate-900">{role.title}</CardTitle>
                              <CardDescription className="text-slate-600">
                                <span className="font-medium">{role.type}</span> · {role.period}
                              </CardDescription>
                              {role.location && (
                                <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                                  <MapPin className="w-3.5 h-3.5" /> {role.location}
                                </div>
                              )}
                            </CardHeader>
                            <CardContent>
                              {role.intro && (
                                <p className="text-sm text-slate-600 leading-relaxed mb-3">{role.intro}</p>
                              )}
                              {role.points.length > 0 && (
                                <ul className="space-y-2 mb-4">
                                  {role.points.map((p, pi) => (
                                    <motion.li
                                      key={pi}
                                      className="flex gap-2 text-sm text-slate-600 leading-relaxed"
                                      initial={{ opacity: 0, x: -16 }}
                                      whileInView={{ opacity: 1, x: 0 }}
                                      viewport={{ once: true, margin: '-60px' }}
                                      transition={{ duration: 0.4, delay: 0.15 + pi * 0.05 }}
                                    >
                                      <ChevronRight className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                                      <span>{p}</span>
                                    </motion.li>
                                  ))}
                                </ul>
                              )}
                              {role.skills.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {role.skills.map((s, si) => (
                                    <Badge key={si} variant="secondary" className="bg-blue-50 text-blue-700">
                                      {s}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </TiltCard>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section id="expertise" className="py-16 sm:py-20 px-4 sm:px-6 bg-white relative z-10" data-testid="expertise-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center space-y-3 sm:space-y-4 mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Core Expertise</h2>
            <div className="h-1 w-20 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full"></div>
            <p className="text-sm sm:text-base lg:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              Testing across multiple domains and technologies
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" style={{ perspective: 1200 }}>
            {expertise.map((item, index) => (
              <Reveal key={index} delay={index * 0.08} className="h-full">
                <TiltCard max={10} className="h-full">
                  <Card className="h-full border-slate-200 bg-white/90 backdrop-blur hover:border-blue-600 hover:shadow-2xl transition-all duration-300 group" data-testid={`expertise-card-${index}`}>
                    <CardHeader>
                      <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors mb-4">
                        {item.icon}
                      </div>
                      <CardTitle className="text-xl">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-600">{item.description}</p>
                    </CardContent>
                  </Card>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-16 sm:py-20 px-4 sm:px-6 bg-transparent relative z-0" data-testid="projects-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center space-y-3 sm:space-y-4 mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Featured Projects</h2>
            <div className="h-1 w-20 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full"></div>
            <p className="text-sm sm:text-base lg:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              Tools and applications I've built for testing and daily work
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8" style={{ perspective: 1200 }}>
            {projects.map((project, index) => (
              <Reveal key={project.id} delay={index * 0.08} className="h-full">
                <TiltCard max={9} className="h-full">
                  <Card className="h-full overflow-hidden border-slate-200 bg-white/90 backdrop-blur hover:border-blue-600 hover:shadow-2xl transition-all duration-300 group" data-testid={`project-card-${project.id}`}>
                    <div className="relative h-48 overflow-hidden bg-slate-100">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl group-hover:text-blue-600 transition-colors">
                        {project.title}
                      </CardTitle>
                      <CardDescription className="text-slate-600">{project.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.tags.map((tag, idx) => (
                          <Badge key={idx} variant="secondary" className="bg-blue-50 text-blue-700">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      {project.github ? (
                        <Button
                          variant="outline"
                          className="w-full border-slate-300 hover:border-blue-600 hover:text-blue-600 hover:bg-blue-50 group/btn"
                          onClick={() => window.open(project.github, '_blank')}
                          data-testid={`project-github-${project.id}`}
                        >
                          <Github className="w-4 h-4 mr-2" />
                          View on GitHub
                          <ExternalLink className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                        </Button>
                      ) : (
                        <div className="w-full py-2 px-4 text-center text-sm text-slate-500 bg-slate-50 rounded-md border border-slate-200">
                          Private Project
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-16 sm:py-20 px-4 sm:px-6 bg-white relative z-10" data-testid="skills-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center space-y-3 sm:space-y-4 mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Technical Skills</h2>
            <div className="h-1 w-20 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full"></div>
            <p className="text-sm sm:text-base lg:text-lg text-slate-600 max-w-2xl mx-auto px-4">
              Tools and technologies I work with
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" style={{ perspective: 1200 }}>
            {skills.map((skillGroup, index) => (
              <Reveal key={index} delay={index * 0.06} className="h-full">
                <TiltCard max={7} className="h-full">
                  <Card className="h-full border-slate-200 bg-white/90 backdrop-blur hover:shadow-lg transition-shadow" data-testid={`skill-group-${index}`}>
                    <CardHeader>
                      <CardTitle className="text-xl text-blue-600">{skillGroup.category}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {skillGroup.items.map((skill, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className="bg-slate-100 text-slate-700 hover:bg-blue-100 hover:text-blue-700 transition-colors"
                          >
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-blue-600 to-cyan-600 text-white relative overflow-hidden" data-testid="contact-section">
        <div className="max-w-4xl mx-auto text-center space-y-6 sm:space-y-8 relative z-10">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold">Let's Connect</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-base sm:text-lg lg:text-xl text-blue-100 px-4">
              Interested in collaborating or discussing quality assurance? Feel free to reach out on LinkedIn.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Magnetic strength={0.4} className="inline-block">
              <Button
                size="lg"
                onClick={() => window.open('https://www.linkedin.com/in/emre-daver', '_blank')}
                className="bg-white text-blue-600 hover:bg-blue-50 hover:scale-105 transition-transform w-full sm:w-auto"
                data-testid="contact-linkedin-button"
              >
                <Linkedin className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                Connect on LinkedIn
                <ChevronRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2" />
              </Button>
            </Magnetic>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-6 sm:py-8 px-4 sm:px-6" data-testid="footer">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm sm:text-base">&copy; 2025 Emre Daver. All rights reserved.</p>
          <p className="mt-2 text-xs sm:text-sm">Computer Engineer | Software Test Engineer</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
